import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useCreateCheckoutSessionMutation,
  useProvisionPaymentSessionMutation,
} from '@backrs_ui/grow-ui/src/redux/subscription/subscriptionApi';

import { Typography, Box, SxProps, Theme, Link, Alert } from '@mui/material';

import { EmbeddedCheckoutComponent } from '../components/EmbeddedCheckout';
import { BASIC_PLAN_ID, SubscriptionPlans } from '../components/SubscriptionPlans';
import { SubscriptionStatus } from '../components/SubscriptionStatus';
import { PaymentPageProps } from '../SubscriptionPageContainer';

const STRINGS = appStrings.modules.subscription.subscriptionPageContainer;

type SubscriptionPageProps = Readonly<{
  subscription?: {
    hasActiveSubscription: boolean;
    subscriptionId?: string | undefined;
    status?: 'active' | 'trialing' | 'past_due' | 'canceled' | undefined;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
    planName?: string;
  };
  onPaymentSuccess?: (sessionId?: string) => void;
  onBasicPlanContinue?: () => void;
  subscriptionView: PaymentPageProps['subscriptionView'];
  refetchSubscription: () => void;
  onCheckoutViewChange?: (isShowingCheckout: boolean) => void;
}>;

export const SubscriptionPage = ({
  subscription,
  onPaymentSuccess,
  onBasicPlanContinue,
  subscriptionView,
  refetchSubscription,
  onCheckoutViewChange,
}: SubscriptionPageProps) => {
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);

  const [createCheckoutSession] = useCreateCheckoutSessionMutation();
  const [provisionPaymentSession] = useProvisionPaymentSessionMutation();
  const navigate = useNavigate();

  const showSubscriptionStatus =
    subscription?.hasActiveSubscription && window.location.pathname === APP_ROUTE.SETTINGS;
  const showEmbeddedCheckoutView =
    !showSubscriptionStatus && showEmbeddedCheckout && checkoutClientSecret;

  // Notify parent when checkout view changes
  useEffect(() => {
    onCheckoutViewChange?.(!!showEmbeddedCheckoutView);
  }, [showEmbeddedCheckoutView, onCheckoutViewChange]);

  const handleSelectPlan = (priceId: string) => {
    setSelectedPriceId(priceId);
    setCheckoutError(null);
  };

  const handleContinue = async () => {
    if (!selectedPriceId) return;

    // Basic isn't a Stripe plan — nothing to purchase or grant, just move on
    if (selectedPriceId === BASIC_PLAN_ID) {
      onBasicPlanContinue?.();

      return;
    }

    setCheckoutLoading(true);
    setCheckoutError(null);

    try {
      // Create embedded checkout session
      const result = await createCheckoutSession({
        priceId: selectedPriceId,
      }).unwrap();

      setCheckoutClientSecret(result.clientSecret);
      setCheckoutSessionId(result.sessionId);
      setShowEmbeddedCheckout(true);
    } catch (error) {
      setCheckoutError('Failed to create checkout session. Please try again.');
      console.error('Checkout Session error:', error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCheckoutSuccess = async () => {
    // If we have an onPaymentSuccess callback (e.g., in onboarding flow), use it instead of redirecting
    if (onPaymentSuccess) {
      onPaymentSuccess(checkoutSessionId || undefined);

      return;
    }

    setShowEmbeddedCheckout(false);
    setCheckoutClientSecret(null);
    refetchSubscription();

    // Provision the payment session to grant access and invalidate the UserPermissions cache.
    // This ensures PageShell's paywall check lets the user through without waiting for the Stripe webhook.
    if (checkoutSessionId) {
      try {
        const result = await provisionPaymentSession({ sessionId: checkoutSessionId }).unwrap();

        if (result.provision_status !== 'success') {
          console.warn('Provision returned non-success status:', {
            sessionId: checkoutSessionId,
            status: result.provision_status,
          });
        }
      } catch (error) {
        console.error('Provision failed after checkout:', { sessionId: checkoutSessionId, error });
      }
    }

    // Redirect to return page with session ID (standalone usage)
    const url = new URL('/subscription/return', window.location.origin);

    if (checkoutSessionId) {
      url.searchParams.set('session_id', checkoutSessionId);
    } else {
      url.searchParams.set('success', 'true');
    }

    // Use React Router navigation for internal routes
    const redirectPath = url.pathname + url.search;

    navigate(redirectPath, { replace: true });
  };

  const handleCheckoutError = (error: string) => {
    setCheckoutError(error);
    setShowEmbeddedCheckout(false);
    setCheckoutClientSecret(null);
  };

  const handleBackToPlans = () => {
    setShowEmbeddedCheckout(false);
    setCheckoutClientSecret(null);
    setCheckoutSessionId(null);
    setCheckoutError(null);
  };

  const renderContent = () => {
    if (showSubscriptionStatus) {
      return <SubscriptionStatus subscription={subscription} />;
    }

    if (showEmbeddedCheckoutView) {
      return (
        <EmbeddedCheckoutComponent
          clientSecret={checkoutClientSecret}
          onSuccess={handleCheckoutSuccess}
          onError={handleCheckoutError}
          handleBackToPlans={handleBackToPlans}
          showBackToPlans={!onPaymentSuccess}
          isConstrainedLayout={!!onPaymentSuccess}
        />
      );
    }

    return (
      <SubscriptionPlans
        currentPlan={subscription?.planName}
        selectedPriceId={selectedPriceId}
        onSelectPlan={handleSelectPlan}
        onContinue={handleContinue}
        continueLoading={checkoutLoading}
        loading={checkoutLoading}
        subscriptionView={subscriptionView}
      />
    );
  };

  return (
    <Box sx={containerSx}>
      <Box>
        {checkoutError && <Alert severity="error">{checkoutError}</Alert>}
        {renderContent()}
      </Box>

      {subscriptionView === 'PAYWALL' && (
        <Box sx={{ pb: 2 }}>
          <Typography variant="text-xs-regular">
            {STRINGS.troubleWithYourSubscription}{' '}
            <Link
              id="subscription-contact-support-link"
              href="mailto:support@backrs.com?subject=Backrs - Subscription Assistance Request"
              sx={{ cursor: 'pointer' }}
            >
              {STRINGS.contactSupport}
            </Link>
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  justifyContent: 'space-between',
  py: backrs.spacing['spacing-2xl'],
});
